(() => {
  const initWishlist = () => {
    const storageKey = 'ti-wishlist-v1';
    const drawer = document.querySelector('#TiWishlistDrawer');
    if (!drawer || drawer.dataset.tiWishlistReady === 'true') return;
    drawer.dataset.tiWishlistReady = 'true';

    let previousFocus = null;

    const readItems = () => {
      try {
        const items = JSON.parse(localStorage.getItem(storageKey) || '[]');
        return Array.isArray(items) ? items.filter((item) => item?.handle && item?.url) : [];
      } catch (_) {
        return [];
      }
    };

    const writeItems = (items) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(items));
      } catch (_) {
        // The current-page controls still remain usable if storage is unavailable.
      }
    };

    const labels = {
      viewDetails: drawer.dataset.labelViewDetails || 'View details',
      remove: drawer.dataset.labelRemove || 'Remove',
      removeItem: drawer.dataset.labelRemoveItem || 'Remove __TITLE__ from wishlist',
      saveItem: drawer.dataset.labelSaveItem || 'Save __TITLE__ to wishlist',
      savedItem: drawer.dataset.labelSavedItem || 'Remove __TITLE__ from wishlist',
      save: drawer.dataset.labelSave || 'Save to wishlist',
      saved: drawer.dataset.labelSaved || 'Saved to wishlist',
    };

    const withTitle = (template, title) => template.replace('__TITLE__', title);

    const formatPrice = (minorUnits) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: 'QAR',
        currencyDisplay: 'code',
        minimumFractionDigits: 2,
      }).format((Number(minorUnits) || 0) / 100);

    const itemFromButton = (button) => ({
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '0',
    });

    const makeItem = (item) => {
      const row = document.createElement('li');
      row.className = 'ti-wishlist-item';

      const image = document.createElement('img');
      image.className = 'ti-wishlist-item__image';
      image.src = item.image || '';
      image.alt = '';
      image.width = 88;
      image.height = 88;
      image.loading = 'lazy';

      const body = document.createElement('div');
      body.className = 'ti-wishlist-item__body';

      const title = document.createElement('a');
      title.className = 'ti-wishlist-item__title';
      title.href = item.url;
      title.textContent = item.title;

      const price = document.createElement('p');
      price.className = 'ti-wishlist-item__price';
      price.textContent = formatPrice(item.price);

      const actions = document.createElement('div');
      actions.className = 'ti-wishlist-item__actions';

      const details = document.createElement('a');
      details.href = item.url;
      details.textContent = labels.viewDetails;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ti-wishlist-item__remove';
      remove.dataset.tiWishlistRemove = item.handle;
      remove.textContent = labels.remove;
      remove.setAttribute('aria-label', withTitle(labels.removeItem, item.title));

      actions.append(details, remove);
      body.append(title, price, actions);
      row.append(image, body);
      return row;
    };

    const render = () => {
      const items = readItems();
      const list = drawer.querySelector('[data-ti-wishlist-items]');
      const empty = drawer.querySelector('[data-ti-wishlist-empty]');
      list.replaceChildren(...items.map(makeItem));
      list.hidden = items.length === 0;
      empty.hidden = items.length > 0;

      document.querySelectorAll('[data-ti-wishlist-count]').forEach((badge) => {
        badge.textContent = String(items.length);
        badge.hidden = items.length === 0;
      });

      document.querySelectorAll('[data-ti-wishlist-product]').forEach((button) => {
        const saved = items.some((item) => item.handle === button.dataset.productHandle);
        const title = button.dataset.productTitle || '';
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', withTitle(saved ? labels.savedItem : labels.saveItem, title));
        const label = button.querySelector('.ti-wishlist-button__label');
        if (label) label.textContent = saved ? labels.saved : labels.save;
      });
    };

    const openDrawer = () => {
      previousFocus = document.activeElement;
      drawer.hidden = false;
      document.body.classList.add('overflow-hidden');
      document
        .querySelectorAll('[data-ti-wishlist-open]')
        .forEach((button) => button.setAttribute('aria-expanded', 'true'));
      drawer.querySelector('.ti-wishlist-drawer__panel').focus();
    };

    const closeDrawer = () => {
      drawer.hidden = true;
      document.body.classList.remove('overflow-hidden');
      document
        .querySelectorAll('[data-ti-wishlist-open]')
        .forEach((button) => button.setAttribute('aria-expanded', 'false'));
      previousFocus?.focus?.();
    };

    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-ti-wishlist-open]');
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        render();
        openDrawer();
        return;
      }

      if (event.target.closest('[data-ti-wishlist-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      const productButton = event.target.closest('[data-ti-wishlist-product]');
      if (productButton) {
        event.preventDefault();
        event.stopPropagation();
        const item = itemFromButton(productButton);
        const items = readItems();
        const index = items.findIndex((saved) => saved.handle === item.handle);
        if (index >= 0) items.splice(index, 1);
        else items.unshift(item);
        writeItems(items);
        render();
        return;
      }

      const remove = event.target.closest('[data-ti-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        writeItems(readItems().filter((item) => item.handle !== remove.dataset.tiWishlistRemove));
        render();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (drawer.hidden) return;
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        const panel = drawer.querySelector('.ti-wishlist-drawer__panel');
        const focusable = [
          ...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        ];
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    render();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist, { once: true });
  } else {
    initWishlist();
  }
})();
